/**
 * `subtasks` → **Settings** (spec M1-B5 §6.2, M2 tier-1 §5.9, card P-123).
 *
 * The `subtasks` column is the link from a board to its **subitems board**; `settings_str` holds
 * `boardIds` (that board), `allowMultipleItems`, `displayType` and `itemTypeName` — *"an i18n key
 * (`column.subtasks.title`), not a literal"* `[cvs§2 subtasks]`, which is why the field below
 * shows the key and says so rather than pretending it is a label to type.
 *
 * `boardIds` is **not** editable: it is written when the subitems board is created and repointing
 * it would orphan every subitem under the column (spec M3-S1). It is shown read-only so the
 * dialog says what the column is linked to.
 *
 * The type is also the one that is not a grid column at all (`gridColumn: false`, P-023): monday
 * renders the ⊕ expander in the sticky region instead of a `Subitems` column. That is a layout
 * rule, not a settings one, so it is not offered here either.
 */
import type { SubtasksSettings } from '@elizmonday/shared';
import {
  SettingsCheckbox,
  SettingsField,
  SettingsSection,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

export function SubtasksSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<SubtasksSettings>) {
  const [draft, patch] = useSettingsDraft<SubtasksSettings>(settings);
  const boardIds = draft.boardIds ?? [];
  return (
    <SettingsForm
      title="Subitems column settings"
      testId="subtasks-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsSection label="Subitems board" />
      <p className="ez-type-settings__empty" data-testid="subtasks-board">
        {boardIds.length === 0
          ? 'No subitems board is linked yet.'
          : boardIds.map((boardId) => String(boardId)).join(', ')}
      </p>
      <SettingsCheckbox
        label="Allow linking to multiple items"
        testId="subtasks-allow-multiple"
        checked={draft.allowMultipleItems !== false}
        onChange={(next) => patch({ allowMultipleItems: next })}
      />
      <SettingsField
        label="Item type name (an i18n key, not a label)"
        testId="subtasks-item-type-name"
        value={draft.itemTypeName ?? ''}
        placeholder="column.subtasks.title"
        onChange={(next) => patch({ itemTypeName: next })}
      />
    </SettingsForm>
  );
}
